import { Add } from '@mui/icons-material';
import { Box, Button, Grid, lighten, darken, TextField, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Table } from '../../components';
import { IAgentCommission, ReactSetState } from '../../models';
import { theme } from '../../styles';

import { MuiTextFieldProps } from '@mui/lab/internal/pickers/PureDateInput';

export interface ICommissionNames {
  commissions: IAgentCommission[];
  setCommissions: ReactSetState<IAgentCommission[]>;
  commissionsCopy: IAgentCommission[];
  selectedCommission: IAgentCommission | null;
  setSelectedCommission: ReactSetState<IAgentCommission | null>;
  setNewElementIndex: any;
  hasChanges: boolean;
  isLoading: boolean;
}
export const CommissionNames: FC<ICommissionNames> = ({
  commissions,
  setCommissions,
  selectedCommission,
  setSelectedCommission,
  setNewElementIndex,
  hasChanges,
  isLoading,
}) => {
  // Non-state
  const classes = useStyles();

  // Network state

  // Non-network state

  const [showNewTableNameInput, setShowNewTableNameInput] = useState<boolean>(false);
  const [newName, setNewName] = useState<string>('');
  const [isNewNameError, setIsNewNameError] = useState(false);
  const nameTextBox = useRef<MuiTextFieldProps>(null);

  const nameIsTaken = () => {
    const names = commissions.map(commission => commission.name.toLowerCase().trim());
    return names.includes(newName.toLowerCase().trim());
  };

  const nameIsEmpty = () => {
    return newName === ''.trim();
  };

  const nameIsValidated = () => {
    return !nameIsTaken() && !nameIsEmpty();
  };

  const errorMessage = () => {
    if (nameIsTaken()) {
      return 'This name is already taken';
    }
    if (nameIsEmpty()) {
      return 'Name cannot be empty';
    }
  };
  const addNewCommission = (name: string) => {
    const newCommission: IAgentCommission = {
      // Negative number to convert to zero when posting. May not be needed, but adding for explicitness.
      agentCommissionId: -Math.round(Math.random() * 1000000),
      agentCommissionTiers: [],
      name: name,
    };
    if (nameIsValidated()) {
      setCommissions(prev => [...prev, newCommission]);
      setNewName('');
      setShowNewTableNameInput(false);
      setSelectedCommission(newCommission);
    } else {
      setIsNewNameError(true);
    }
  };

  useEffect(() => {
    if (nameIsValidated()) {
      setIsNewNameError(false);
    } else {
      setIsNewNameError(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newName]);

  const commissionNamesColumns = useMemo(() => {
    return [
      {
        id: 'header',
        accessor: '',
        Header: 'Commission Tables',
        disableSortBy: true,
        sort: false,
        Cell: ({
          cell: {
            row: { original },
          },
        }: {
          cell: { row: { original: IAgentCommission } };
        }) => {
          const row = original;
          return (
            <Box>
              <Grid
                item
                key={row.agentCommissionId}
                sx={theme => ({
                  transition: 'padding 160ms ease-in',
                  borderRadius: 1,
                  padding: theme.spacing(0.465),
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: lighten(theme.frankie.neonYellow, 0.8),
                    paddingInline: theme.spacing(1),
                  },
                  // Display Frankie-themed background if this is the selected table.
                  ...(selectedCommission?.name === row.name
                    ? {
                        backgroundColor: lighten(theme.frankie.neonYellow, 0.6),
                        borderLeft: '5px solid',
                        borderColor: theme.frankie.neonYellow,
                        paddingInline: theme.spacing(1),
                        '.MuiTypography-root': {
                          fontWeight: '700',
                        },
                      }
                    : {}),
                })}
                onClick={() => {
                  setSelectedCommission(row);
                  setNewElementIndex(-1);
                }}
              >
                <Typography
                  sx={theme => ({
                    color: theme.frankie.searchBlue,
                  })}
                >
                  {row.name}
                </Typography>
              </Grid>
            </Box>
          );
        },
      },
    ];
  }, [selectedCommission?.name, setNewElementIndex, setSelectedCommission]);

  return (
    <>
      <Table
        data={commissions}
        columns={commissionNamesColumns}
        isLoading={isLoading}
        headerClasses={classes.header}
        hidePagination
        hideTableHeader={true}
      />
      {showNewTableNameInput && (
        <TextField
          label="Name"
          inputRef={nameTextBox}
          className={classes.CommissionTableName}
          fullWidth
          value={newName}
          onBlur={() => addNewCommission(newName)}
          onChange={({ target: { value } }) => setNewName(value)}
          onKeyPress={e => {
            if (e.key === 'Enter') {
              addNewCommission(newName);
            }
          }}
          error={isNewNameError}
          helperText={isNewNameError && errorMessage()}
        />
      )}
      {!isLoading && (
        <Button
          className={classes.addCommissionTableButton}
          onClick={() => setShowNewTableNameInput(true)}
          endIcon={<Add />}
        >
          Add Commission Table
        </Button>
      )}
    </>
  );
};

const useStyles = makeStyles(() => ({
  header: {
    textAlign: 'center',
  },
  addCommissionTableButton: {
    transition: 'padding 160ms ease-in',
    width: '100%',
    color: theme.frankie.searchBlue,
    backgroundColor: theme.palette.grey[300],
    padding: theme.spacing(0.465),
    fontSize: 16,
    fontWeight: 'bold',
    textAlign: 'left',
    marginTop: 6,
    justifyContent: 'space-around',

    '&:hover': {
      backgroundColor: darken(theme.palette.grey[300], 0.1),
      paddingInline: theme.spacing(1, 0.535),
    },
  },
  CommissionTableName: {
    margin: theme.spacing(1, 0),
  },
}));
